import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/Header';
import NuevaAltaForm from '../../components/altas/NuevaAltaForm';
import AltasTable from '../../components/altas/AltasTable';
import EjecutivasModal from '../../components/altas/EjecutivasModal';
import EditarAltaModal from '../../components/altas/EditarAltaModal';
import ImportarModal from '../../components/altas/ImportarModal';
import altasService, { errorMessage } from '../../services/altasService';
import { resumenSync } from '../../components/altas/altasUi';
import {
  Alta,
  Ejecutiva,
  FiltroEstadoAlta,
  DecisionRevisar,
  RevisionEnCurso,
  CreateAltaPayload,
  UpdateAltaPayload,
  FilaImportar,
  FilaRechazada,
  Sincronizacion,
  OrdenAltas,
} from '../../types/Altas';
import {
  Alert,
  Badge,
  Button,
  Card,
  IconSwap,
  Modal,
  staggerParent,
  enterChild,
} from '../../components/ui';
import { RefreshIcon, SpinnerIcon } from '../../components/icons';

const POR_PAGINA = 25;

/**
 * Pestaña "Cargar altas": reemplaza al Excel del jefe comercial. Acá se
 * cargan las ventas por ejecutiva; el cruce con Salesforce y el reporte
 * salen de estos datos, nunca al revés.
 */
const AltasPage: React.FC = () => {
  const [ejecutivas, setEjecutivas] = useState<Ejecutiva[]>([]);
  const [planes, setPlanes] = useState<string[]>([]);
  const [altas, setAltas] = useState<Alta[]>([]);
  const [filtroEjecutiva, setFiltroEjecutiva] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [orden, setOrden] = useState<OrdenAltas>('venta_desc');
  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState('');
  const [pagina, setPagina] = useState(1);
  const [total, setTotal] = useState(0);

  // El esqueleto se muestra solo hasta la primera respuesta: después la tabla queda montada
  // aunque se vuelva a pedir, para no perder el foco del buscador mientras se tipea.
  const [primeraCarga, setPrimeraCarga] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  // Alta y decisión ("Es venta" / "Ya tenía plan") en camino: el botón apretado muestra el spinner.
  const [revisando, setRevisando] = useState<RevisionEnCurso | null>(null);
  // El estado tarda un render en verse: dos clics en el mismo instante lo pasarían. El ref no.
  const revisandoRef = useRef(false);
  const [ultimaSync, setUltimaSync] = useState<Sincronizacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const [ejecutivasAbierto, setEjecutivasAbierto] = useState(false);
  const [importarAbierto, setImportarAbierto] = useState(false);
  const [altaAEditar, setAltaAEditar] = useState<Alta | null>(null);
  const [altaABorrar, setAltaABorrar] = useState<Alta | null>(null);

  // Cambiar un filtro o el orden vuelve a la primera página en el mismo cambio,
  // para no pedir primero la página vieja con el filtro nuevo.
  const cambiarFiltroEjecutiva = (v: string) => {
    setFiltroEjecutiva(v);
    setPagina(1);
  };
  const cambiarFiltroEstado = (v: string) => {
    setFiltroEstado(v);
    setPagina(1);
  };
  const cambiarOrden = (v: OrdenAltas) => {
    setOrden(v);
    setPagina(1);
  };
  // La búsqueda espera a que se deje de tipear antes de pedir al servidor, y vuelve a la primera página.
  const qRef = useRef(q);
  qRef.current = q;
  useEffect(() => {
    const t = window.setTimeout(() => {
      const texto = busqueda.trim();
      if (texto === qRef.current) return;
      setQ(texto);
      setPagina(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [busqueda]);

  // Cada pedido lleva un número; solo se aplica la respuesta del último, así una
  // respuesta lenta de un filtro anterior no pisa la tabla.
  const ultimoPedido = useRef(0);

  const traerAltas = useCallback(async () => {
    const pedido = ultimoPedido.current + 1;
    ultimoPedido.current = pedido;
    const r = await altasService.listarAltas({
      ejecutivaId: filtroEjecutiva ? Number(filtroEjecutiva) : undefined,
      estado: (filtroEstado || undefined) as FiltroEstadoAlta | undefined,
      orden,
      q: q || undefined,
      limit: POR_PAGINA,
      offset: (pagina - 1) * POR_PAGINA,
    });
    if (pedido !== ultimoPedido.current) return;
    setAltas(r.altas);
    setTotal(r.total);
    // Si se borró la última fila de la última página, la página ya no existe: ir a la anterior.
    const paginas = Math.max(1, Math.ceil(r.total / POR_PAGINA));
    if (pagina > paginas) setPagina(paginas);
  }, [filtroEjecutiva, filtroEstado, orden, q, pagina]);

  // El refresco diferido llama siempre al traerAltas vigente, no al del momento en que se programó.
  const traerRef = useRef(traerAltas);
  useEffect(() => {
    traerRef.current = traerAltas;
  }, [traerAltas]);

  const traerEjecutivas = useCallback(async () => {
    setEjecutivas(await altasService.listarEjecutivas());
  }, []);

  // Carga inicial: planes y ejecutivas una vez; las altas cada vez que cambian los filtros.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const [p, e, s] = await Promise.all([
          altasService.listarPlanes(),
          altasService.listarEjecutivas(),
          altasService.ultimaSincronizacion(),
        ]);
        if (cancelado) return;
        setPlanes(p);
        setEjecutivas(e);
        setUltimaSync(s);
      } catch (err) {
        if (!cancelado)
          setError(err instanceof Error ? err.message : 'No se pudo cargar la pantalla');
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        await traerAltas();
      } catch (err) {
        if (!cancelado)
          setError(err instanceof Error ? err.message : 'No se pudieron cargar las altas');
      } finally {
        if (!cancelado) setPrimeraCarga(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [traerAltas]);

  // ── Acciones ─────────────────────────────────────────────────────────────

  // Después de guardar, la tabla se vuelve a pedir; si ese pedido falla, la acción ya
  // se hizo igual, así que se avisa sin dejar colgado a quien la disparó.
  const refrescar = async () => {
    try {
      await traerAltas();
    } catch (err) {
      setError(errorMessage(err, 'Se guardó, pero no se pudo refrescar la lista'));
    }
  };

  const crearAlta = async (payload: CreateAltaPayload): Promise<boolean> => {
    setGuardando(true);
    setError(null);
    setAviso(null);
    const res = await altasService.crearAlta(payload);
    setGuardando(false);
    if (!res.success) {
      setError(res.error ?? 'No se pudo cargar el alta');
      return false;
    }
    setAviso(`Alta cargada: DNI ${res.alta?.dni} para ${res.alta?.ejecutiva}.`);
    await refrescar();
    refrescarTrasSync();
    return true;
  };

  /**
   * El cruce que se dispara al cargar corre en segundo plano en el servidor,
   * así que la tabla se vuelve a pedir un momento después para mostrar el
   * estado nuevo sin que la persona tenga que refrescar.
   */
  const refrescoPendiente = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (refrescoPendiente.current !== null) window.clearTimeout(refrescoPendiente.current);
    },
    [],
  );

  const refrescarTrasSync = () => {
    if (refrescoPendiente.current !== null) window.clearTimeout(refrescoPendiente.current);
    refrescoPendiente.current = window.setTimeout(async () => {
      refrescoPendiente.current = null;
      try {
        await traerRef.current();
        setUltimaSync(await altasService.ultimaSincronizacion());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudo refrescar la lista');
      }
    }, 2500);
  };

  const sincronizar = async () => {
    setSincronizando(true);
    setError(null);
    setAviso(null);
    const res = await altasService.sincronizar();
    setSincronizando(false);
    if (!res.success) {
      setError(res.error ?? 'No se pudo sincronizar con Salesforce');
      return;
    }
    if (res.ultima) setUltimaSync(res.ultima);
    if (res.ultima?.error) setError(`Salesforce respondió con error: ${res.ultima.error}`);
    await refrescar();
  };

  const importarAltas = async (
    ejecutivaId: number,
    filas: FilaImportar[],
  ): Promise<{ creadas: number; rechazadas: FilaRechazada[] } | { error: string }> => {
    setError(null);
    setAviso(null);
    const res = await altasService.importarAltas(ejecutivaId, filas);
    if (!res.success) {
      // El error se muestra adentro del modal: un aviso en la página queda tapado.
      return { error: res.error ?? 'No se pudieron importar las altas' };
    }
    await refrescar();
    if (res.creadas?.length) refrescarTrasSync();
    return { creadas: res.creadas?.length ?? 0, rechazadas: res.rechazadas ?? [] };
  };

  const actualizarAlta = async (id: number, payload: UpdateAltaPayload): Promise<boolean> => {
    setError(null);
    const res = await altasService.actualizarAlta(id, payload);
    if (!res.success) {
      setError(res.error ?? 'No se pudo corregir el alta');
      return false;
    }
    await refrescar();
    if (res.alta?.estado === 'pendiente') refrescarTrasSync();
    return true;
  };

  const borrarAlta = async () => {
    if (!altaABorrar) return;
    setError(null);
    const res = await altasService.borrarAlta(altaABorrar.id);
    setAltaABorrar(null);
    if (!res.success) {
      setError(res.error ?? 'No se pudo borrar el alta');
      return;
    }
    await refrescar();
  };

  // La app marcó la venta; el jefe decide. Lo que decida queda en admin_actions.
  const revisarAlta = async (alta: Alta, decision: DecisionRevisar) => {
    if (revisandoRef.current) return;
    revisandoRef.current = true;
    setError(null);
    setAviso(null);
    setRevisando({ id: alta.id, decision });
    try {
      const res = await altasService.revisarAlta(alta.id, decision);
      if (!res.success) {
        setError(res.error ?? 'No se pudo guardar la decisión');
        return;
      }
      setAviso(
        decision === 'venta'
          ? `DNI ${alta.dni}: queda como venta de ${alta.ejecutiva}.`
          : `DNI ${alta.dni}: ya tenía el plan, pasa a "Ya era cliente" y no se comisiona.`,
      );
      await refrescar();
    } finally {
      revisandoRef.current = false;
      setRevisando(null);
    }
  };

  const crearEjecutiva = async (nombre: string): Promise<boolean> => {
    setError(null);
    const res = await altasService.crearEjecutiva(nombre);
    if (!res.success) {
      setError(res.error ?? 'No se pudo crear el ejecutivo');
      return false;
    }
    await traerEjecutivas();
    return true;
  };

  const activarEjecutiva = async (id: number, activa: boolean) => {
    setError(null);
    const res = await altasService.activarEjecutiva(id, activa);
    if (!res.success) {
      setError(res.error ?? 'No se pudo actualizar el ejecutivo');
      return;
    }
    await traerEjecutivas();
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />

      <main className="container mx-auto flex-grow px-4 py-8">
        <motion.div
          variants={staggerParent}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          <motion.div
            variants={enterChild}
            className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <h1 className="font-poppins text-2xl font-semibold text-gappex-secondary">
                Cargar altas
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Qué vendió cada ejecutivo. Cada DNI se carga una sola vez; el plan y la fecha reales
                se confirman contra Salesforce.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs tabular-nums text-slate-400">
                  {ultimaSync
                    ? resumenSync(ultimaSync)
                    : 'Todavía no se sincronizó con Salesforce.'}
                </span>
                {ultimaSync?.error && (
                  <Badge tone="negative" title={ultimaSync.error}>
                    Falló la última
                  </Badge>
                )}
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={sincronizar}
              disabled={sincronizando}
              className="shrink-0"
              title="Consulta Salesforce por las altas pendientes y con plan distinto, y actualiza si las ventas confirmadas siguen activas"
              icon={
                <IconSwap swapKey={sincronizando ? 'busy' : 'idle'}>
                  {sincronizando ? <SpinnerIcon /> : <RefreshIcon />}
                </IconSwap>
              }
            >
              {sincronizando ? 'Sincronizando…' : 'Sincronizar ahora'}
            </Button>
          </motion.div>

          <AnimatePresence initial={false}>
            {error && (
              <Alert
                key="error"
                tone="error"
                title="No se pudo completar la acción"
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}
            {aviso && (
              <Alert key="aviso" tone="info" onClose={() => setAviso(null)}>
                {aviso}
              </Alert>
            )}
          </AnimatePresence>

          <motion.div variants={enterChild}>
            <NuevaAltaForm
              ejecutivas={ejecutivas}
              planes={planes}
              guardando={guardando}
              onCrear={crearAlta}
              onNuevaEjecutiva={() => setEjecutivasAbierto(true)}
              onPegar={() => setImportarAbierto(true)}
            />
          </motion.div>

          <motion.div variants={enterChild}>
            {primeraCarga ? (
              <Card className="animate-pulse p-6">
                <div className="h-40 rounded-xl bg-slate-100" />
              </Card>
            ) : (
              <AltasTable
                altas={altas}
                ejecutivas={ejecutivas}
                filtroEjecutiva={filtroEjecutiva}
                filtroEstado={filtroEstado}
                onFiltroEjecutiva={cambiarFiltroEjecutiva}
                onFiltroEstado={cambiarFiltroEstado}
                busqueda={busqueda}
                onBusqueda={setBusqueda}
                busquedaAplicada={q}
                onEditar={setAltaAEditar}
                onBorrar={setAltaABorrar}
                onRevisar={revisarAlta}
                revisando={revisando}
                orden={orden}
                onOrden={cambiarOrden}
                total={total}
                pagina={pagina}
                porPagina={POR_PAGINA}
                onPagina={setPagina}
              />
            )}
          </motion.div>
        </motion.div>
      </main>

      <AnimatePresence>
        {ejecutivasAbierto && (
          <EjecutivasModal
            key="ejecutivas"
            ejecutivas={ejecutivas}
            onCrear={crearEjecutiva}
            onActivar={activarEjecutiva}
            onClose={() => setEjecutivasAbierto(false)}
          />
        )}
        {importarAbierto && (
          <ImportarModal
            key="importar"
            ejecutivas={ejecutivas}
            planes={planes}
            onImportar={importarAltas}
            onClose={() => setImportarAbierto(false)}
          />
        )}
        {altaAEditar && (
          <EditarAltaModal
            key="editar"
            alta={altaAEditar}
            ejecutivas={ejecutivas}
            planes={planes}
            onGuardar={actualizarAlta}
            onClose={() => setAltaAEditar(null)}
          />
        )}
        {altaABorrar && (
          <Modal key="borrar" titulo="Borrar alta" onClose={() => setAltaABorrar(null)}>
            <p className="text-sm text-gray-600">
              Se borra el alta del DNI{' '}
              <span className="font-semibold tabular-nums">{altaABorrar.dni}</span> cargada para{' '}
              {altaABorrar.ejecutiva}. Si fue una venta real, cargala de nuevo con el ejecutivo
              correcto.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setAltaABorrar(null)}>
                Cancelar
              </Button>
              <Button type="button" variant="danger" onClick={borrarAlta}>
                Borrar
              </Button>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AltasPage;
